<template>
    <form
        data-test-id="frequency-set-form"
        @submit.prevent="createFrequencySet"
    >
        <label for="name">Import Existing Frequency Settings (Optional)</label>
        <div class="form-group">
            <div class="import-frequency-settings">
                <frequency-set-select
                    v-model="selectedFrequencySetId"
                    :frequency-sets="frequencySets"
                    placeholder="Import Existing Frequency Set"
                    test-id="frequency-set-list"
                />
                <button
                    class="btn btn-medium"
                    data-test-id="import-frequency-set-button"
                    :disabled="selectedFrequencySetId === null"
                    @click.prevent="importSelectedFrequencySet()"
                >
                    Import
                </button>
            </div>
        </div>
        <div class="form-group">
            <label for="name">Name</label>
            <input
                id="name"
                v-model="name"
                data-test-id="name"
                :maxlength="maxNameLength"
                required
                type="text"
            />
        </div>

        <div class="form-group">
            <label for="max_power">Max Power</label>
            <select
                id="max_power"
                v-model="max_power"
                data-test-id="max-power"
                required
            >
                <option value="0.5W">0.5W</option>
                <option value="1W">1W</option>
                <option value="2W">2W</option>
                <option value="4W">4W</option>
                <option value="5W">5W</option>
            </select>
        </div>

        <div class="form-group bandwidth-section">
            <label for="bandwidth">Bandwidth</label>
            <select
                id="bandwidth"
                v-model="chosenBandwidthValue"
                data-test-id="bandwidth"
                required
            >
                <option
                    v-for="(choice, index) in bandwidths"
                    :key="`b${index}`"
                    :data-test-id="`bandwidth-choice-${index}`"
                    :value="choice.value"
                >
                    {{ choice.value }}
                </option>
            </select>
            <span
                v-if="chosenBandwidth"
                :class="['section-info', { error: anyChannelHasBadSpacing() }]"
                data-test-id="bandwidth-section-info"
            >
                Channels should be spaced {{ chosenBandwidth.spacing }}MHz apart for the chosen bandwidth:
                {{ chosenBandwidth.value }}.
            </span>
        </div>

        <div
            v-if="ntiaFeatureEnabled"
            class="form-group frequency-range-section"
        >
            <label for="frequency_range">Frequency</label>
            <select
                id="frequency_range"
                v-model="chosenFrequencyRangeIndex"
                data-test-id="frequency-range"
                required
            >
                <option
                    v-for="(range, index) in allowableFrequencyRanges"
                    :key="`f${index}`"
                    :value="index"
                    :data-test-id="`frequency-range-choice-${index}`"
                >
                    {{ range.value }}
                </option>
            </select>
            <span
                v-if="chosenFrequencyRangeIndex !== null"
                :class="['section-info', { error: anyChannelOutOfRange() }]"
                data-test-id="frequency-range-section-info"
            >
                <template v-if="chosenRangeIsAdvanced()">
                    Your frequency selection must be within a valid range.
                </template>
                <template v-else>
                    Your frequency selection must be within the range selected:
                    {{ frequencyRanges[chosenFrequencyRangeIndex].value }}.
                </template>
            </span>
            <span
                v-if="chosenRangeIsAdvanced()"
                class="section-info"
            >
                Advanced Mode allows you to program any frequencies in any of our supported bands in a single frequency
                set. Range and performance may be severely impacted by using frequencies from separate ranges. Use this
                mode at your own risk.
            </span>
        </div>

        <div class="form-group channel-section">
            <h3 class="channel-type-header control">Control Channels ({{ controlChannels.length }})</h3>
            <div
                v-for="(channel, index) in controlChannels"
                :key="`c${index}`"
                :class="['channel-field-container', { error: channelHasError(channel) }]"
            >
                <input
                    v-model="channel.frequency"
                    type="text"
                    :class="['channel-field', { error: channelHasError(channel) }]"
                    placeholder="Frequency (MHz)"
                    maxlength="9"
                    required
                    :data-test-id="`control-channel-${index}`"
                    @keyup="enforceDecimal($event)"
                />
            </div>
            <a
                v-if="controlChannels.length < 3"
                :class="['btn', 'add-channel-btn', { disabled: anyChannelHasError() }]"
                href="#"
                data-test-id="add-control-channel-button"
                @click.prevent="addControlChannel"
            >
                <span class="icon-add"></span> Add
            </a>
            <a
                v-if="controlChannels.length > 1"
                class="btn remove-channel-btn"
                data-test-id="remove-control-channel-button"
                href="#"
                @click.prevent="removeControlChannel"
            >
                <span class="icon-remove"></span> Remove
            </a>

            <h3 class="channel-type-header data">Data Channels ({{ dataChannels.length }})</h3>
            <div
                v-for="(channel, index) in dataChannels"
                :key="`d${index}`"
                :class="['channel-field-container', { error: channelHasError(channel) }]"
            >
                <input
                    v-model="channel.frequency"
                    type="text"
                    :class="['channel-field', { error: channelHasError(channel) }]"
                    placeholder="Frequency (MHz)"
                    maxlength="9"
                    required
                    :data-test-id="`data-channel-${index}`"
                    @keyup="enforceDecimal($event)"
                />
            </div>
            <a
                v-if="dataChannels.length < 13"
                :class="['btn', 'add-channel-btn', { disabled: anyChannelHasError() }]"
                data-test-id="add-data-channel-button"
                href="#"
                @click.prevent="addDataChannel"
            >
                <span class="icon-add"></span> Add
            </a>
            <a
                v-if="dataChannels.length > 1"
                class="btn remove-channel-btn"
                data-test-id="remove-data-channel-button"
                href="#"
                @click.prevent="removeDataChannel"
            >
                <span class="icon-remove"></span> Remove
            </a>
        </div>

        <form-errors
            :response="errorResponse"
            @warningsDetected="warningsDetected"
        ></form-errors>

        <div
            v-if="showSaveAndCancel"
            class="form-group save-and-cancel"
        >
            <button
                :class="['btn', 'btn-secondary']"
                data-test-id="cancel-button"
                @click.prevent="$emit('cancel-button-clicked')"
            >
                <div>Cancel</div>
            </button>
            <button
                :class="['btn', 'btn-primary', { active: submitted }]"
                :disabled="anyChannelHasError()"
                data-test-id="save-button"
                type="submit"
            >
                <div>Save</div>
            </button>
        </div>
        <div
            v-else
            class="form-group"
        >
            <button
                :class="['btn', 'btn-primary', { active: submitted }]"
                :disabled="anyChannelHasError()"
                data-test-id="submit-button"
                type="submit"
            >
                <div>Create</div>
            </button>
        </div>
    </form>
</template>

<script>
import canConfirm from '@/mixins/canConfirm'
import FormErrors from '@/components/FormErrors.vue'
import FrequencySetSelect from '@/components/FrequencySetSelect.vue'
import canToast from '@/mixins/canToast'
import hasBackButton from '@/mixins/hasBackButton'
import enforcesDecimals from '@/mixins/enforcesDecimals'
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'
import frequencySetSpacing from '@/mixins/frequencySetSpacing'
import frequencyRanges from '@/mixins/frequencyRanges'
import featureFlags from '@/constants/feature-flags'
import frequencySetTypes from '@/constants/frequency-set-types.js'

export default {
    components: {
        FormErrors,
        FrequencySetSelect,
    },

    mixins: [
        canConfirm,
        canToast,
        hasBackButton,
        enforcesDecimals,
        needsOrganizationSlug,
        frequencySetSpacing,
        frequencyRanges,
    ],

    props: {
        createEndpoint: {
            type: String,
            required: false,
        },
        showSaveAndCancel: {
            type: Boolean,
            required: false,
            default: false,
        },
        frequencySet: {
            type: Object,
            required: false,
        },
    },

    emits: ['frequency-set-created', 'cancel-button-clicked'],

    data() {
        return {
            name: null,
            max_power: null,
            chosenBandwidthValue: null,
            controlChannels: [
                {
                    type: 'Control',
                    frequency: '',
                },
            ],
            dataChannels: [
                {
                    type: 'Data',
                    frequency: '',
                },
            ],
            ignore_exact_spacing_warning: false,
            errorResponse: null,
            submitted: false,
            frequencySets: [],
            selectedFrequencySetId: null,
            maxNameLength: 20,
        }
    },

    computed: {
        bandwidths() {
            return this.bandwidthChoices.filter((choice) => !choice.deprecated)
        },

        ntiaFeatureEnabled() {
            return this.$store.getters.hasFeatureFlag(featureFlags.NTIA_FEATURE_ENABLED)
        },
    },

    mounted() {
        this.fetchFrequencySets()
        if (this.frequencySet) {
            this.name = this.frequencySet.name
            this.max_power = this.frequencySet.max_power
            this.chosenBandwidthValue = this.frequencySet.bandwidth
            this.originalBandwidthValue = this.frequencySet.bandwidth
            const { channels } = this.frequencySet
            this.controlChannels = channels.filter((channel) => channel.type === 'Control')
            this.dataChannels = channels.filter((channel) => channel.type === 'Data')
        }
    },

    methods: {
        createFrequencySet() {
            this.submitted = true
            const channels = this.controlChannels.concat(this.dataChannels)
            const hasCustomEndpoint = this.createEndpoint !== undefined
            const endpoint = hasCustomEndpoint
                ? this.createEndpoint
                : `/${this.organizationSlug}/frequency-sets?type=${frequencySetTypes.X_SERIES.type}`

            this.$http
                .post(endpoint, {
                    name: this.name,
                    max_power: this.max_power,
                    bandwidth: this.chosenBandwidthValue,
                    channels,
                    ignore_exact_spacing_warning: this.ignore_exact_spacing_warning,
                })
                .then((data) => {
                    this.submitted = false
                    this.$emit('frequency-set-created', data.data)
                    if (!hasCustomEndpoint) {
                        this.toast('Frequency set successfully created!')
                        this.$router.push(`/${this.organizationSlug}/frequency-sets`)
                    }
                })
                .catch((error) => {
                    this.submitted = false
                    this.ignore_exact_spacing_warning = false

                    this.errorResponse = error.response
                })
        },

        addControlChannel() {
            if (this.controlChannels.length >= 3 || this.anyChannelHasError()) return

            this.controlChannels.push({
                type: 'Control',
                frequency: '',
            })
        },

        removeControlChannel() {
            if (this.controlChannels.length <= 1) return

            this.controlChannels.pop()
        },

        addDataChannel() {
            if (this.dataChannels.length >= 13 || this.anyChannelHasError()) return

            this.dataChannels.push({
                type: 'Data',
                frequency: '',
            })
        },

        removeDataChannel() {
            if (this.dataChannels.length <= 1) return

            this.dataChannels.pop()
        },

        async warningsDetected(warnings) {
            const willSubmit = await this.confirm({
                message: `${warnings}\n\nAre you sure you want to create this Frequency Set?`,
                acceptLabel: 'Yes - Create',
            })

            if (willSubmit) {
                this.ignore_exact_spacing_warning = true
                this.createFrequencySet()
            }
        },

        channelHasError(channel) {
            /* eslint-disable no-else-return */
            if (this.ntiaFeatureEnabled) {
                return this.channelOutOfRange(channel) || this.channelHasBadSpacing(channel)
            } else {
                return this.channelHasBadSpacing(channel)
            }
            /* eslint-enable no-else-return */
        },

        anyChannelHasError() {
            /* eslint-disable no-else-return */
            if (this.ntiaFeatureEnabled) {
                return this.anyChannelOutOfRange() || this.anyChannelHasBadSpacing()
            } else {
                return this.anyChannelHasBadSpacing()
            }
            /* eslint-enable no-else-return */
        },

        fetchFrequencySets() {
            return this.$http
                .get(`/${this.organizationSlug}/frequency-sets/names?type=${frequencySetTypes.X_SERIES.type}`)
                .then(({ data }) => {
                    this.frequencySets = data
                })
        },

        importSelectedFrequencySet() {
            this.errorResponse = null
            return this.$http
                .get(`/${this.organizationSlug}/frequency-sets/${this.selectedFrequencySetId}`)
                .then(({ data }) => {
                    this.name = `Copy of ${data.name}`.substring(0, this.maxNameLength)
                    this.max_power = data.max_power
                    this.chosenBandwidthValue = data.bandwidth
                    const { channels } = data
                    this.controlChannels = channels.filter((channel) => channel.type === 'Control')
                    this.dataChannels = channels.filter((channel) => channel.type === 'Data')
                })
                .catch((error) => {
                    this.toast('There was a problem importing the frequency set.', 'error')
                })
        },
    },
}
</script>

<style scoped lang="scss">
@use '@sass/_variables.scss' as *;

.bandwidth-section,
.frequency-range-section {
    .section-info {
        margin-top: 12px;
        font-size: 14px;
        line-height: 20px;
        display: flex;

        &:before {
            content: '';
            background-image: url(@images/icon-info.svg);
            background-size: 17px 17px;
            background-repeat: no-repeat;
            background-position: top left;
            width: 17px;
            height: 17px;
            margin-right: 14px;
            display: block;
            flex-shrink: 0;
        }

        &.error {
            color: $red;
            font-family: 'Avenir Heavy';

            &:before {
                background-image: url(@images/icon-alert.svg);
            }
        }
    }
}

.channel-type-header {
    padding-top: 25px;
    border-top: 1px solid #707070;
    margin-bottom: 10px;
    font-family: 'Avenir Heavy';
    font-size: 0.9125em;
}

.channel-type-header.control {
    color: #75bac4;
}

.channel-type-header.data {
    color: #ffb571;
}

.channel-type-header:last-of-type {
    margin-top: 25px;
}

.channel-field-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    position: relative;

    .channel-field {
        margin-bottom: 25px;
    }

    &.error:after {
        content: '';
        background-image: url(@images/icon-alert.svg);
        background-size: 17px 17px;
        background-repeat: no-repeat;
        background-position: top left;
        width: 17px;
        height: 17px;
        display: block;
        flex-shrink: 0;
        position: absolute;
        right: 10px;
        top: 10px;
    }
}

.add-channel-btn,
.remove-channel-btn {
    float: none;
    padding: 0 20px;
    font-size: 0.75em;
    line-height: 30px;

    &.disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
}

.add-channel-btn .icon-add,
.remove-channel-btn .icon-remove {
    top: 2px;
    font-size: 1rem;
}

.remove-channel-btn {
    margin-left: 5px;
}

.import-frequency-settings {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    gap: 10px;

    .p-select {
        width: 275px;
    }
}

.save-and-cancel {
    text-align: right;

    button {
        margin-left: 20px;
    }
}
</style>
